import { cn } from "@/lib/utils";

interface SkeletonProps {
  className?: string;
  /** data-testid for E2E tests */
  testId?: string;
}

/** Bone-white animated placeholder — Amazon-style skeleton loading */
export function Skeleton({ className, testId }: SkeletonProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "animate-pulse rounded-md bg-amber-100/60",
        className
      )}
    />
  );
}

/** Product card skeleton — matches actual ProductCard dimensions */
export function ProductCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-amber-100 bg-white p-4">
      <Skeleton className="aspect-square w-full rounded-lg" testId="skeleton-product-image" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <div className="mt-2 flex items-center justify-between">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
    </div>
  );
}

/** Grid of product card skeletons */
export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }, (_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Cart item skeleton — matches CartItem row */
export function CartItemSkeleton() {
  return (
    <div className="flex items-center gap-4 border-b border-amber-50 py-4">
      <Skeleton className="h-20 w-20 shrink-0 rounded-lg" />
      <div className="flex flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>
  );
}

/** Cart page skeleton */
export function CartSkeleton() {
  return (
    <div className="space-y-4" data-testid="skeleton-cart">
      <Skeleton className="h-8 w-48" />
      {Array.from({ length: 3 }, (_, i) => (
        <CartItemSkeleton key={i} />
      ))}
      <div className="mt-6 space-y-3 rounded-xl border border-amber-100 bg-amber-50/30 p-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="mt-4 h-12 w-full rounded-lg" />
      </div>
    </div>
  );
}

/** Hero section skeleton */
export function HeroSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-2xl" data-testid="skeleton-hero">
      <Skeleton className="aspect-[21/9] w-full sm:aspect-[3/1]" />
      <div className="absolute inset-0 flex flex-col items-start justify-center gap-4 p-8 sm:p-12">
        <Skeleton className="h-4 w-24 rounded-full" />
        <Skeleton className="h-10 w-3/4 sm:h-14" />
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-12 w-40 rounded-lg" />
      </div>
    </div>
  );
}

/** Page-level skeleton (full layout placeholder) */
export function PageSkeleton() {
  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-8" data-testid="skeleton-page">
      <Skeleton className="h-10 w-64" />
      <div className="grid gap-6 md:grid-cols-3">
        <div className="space-y-4 md:col-span-2">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** Checkout step skeleton */
export function CheckoutSkeleton() {
  return (
    <div className="space-y-6" data-testid="skeleton-checkout">
      <Skeleton className="h-8 w-56" />
      <div className="space-y-4 rounded-xl border border-amber-100 p-6">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
      <Skeleton className="h-12 w-full rounded-lg" />
    </div>
  );
}

/** Table skeleton for order lists */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3" data-testid="skeleton-table">
      <div className="flex gap-4 border-b border-amber-100 pb-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 flex-1" />
        <Skeleton className="h-4 w-20" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 py-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-8 w-20 rounded-md" />
        </div>
      ))}
    </div>
  );
}
